import { useState } from 'react';
import { searchSites, enrichSites, type SiteInfo } from '@/graph/modules/sites';
import { ErrorNotice } from './ErrorNotice';

/**
 * Search-and-select control for choosing which SharePoint sites to scan. Uses the
 * keyword `/sites?search=` endpoint (Sites.Read.All), avoiding the admin-gated
 * `getAllSites` route that 403s in many tenants. Results can be filtered by owner,
 * size, creation date, and last-activity date, and selected individually or all at
 * once.
 */
function fmtSize(b?: number): string {
  if (b == null) return '—';
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(1)} GB`;
  if (b >= 1048576) return `${(b / 1048576).toFixed(0)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}
function fmtDate(iso?: string): string {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

export function SitePicker({
  selected,
  onChange,
  disabled,
}: {
  selected: SiteInfo[];
  onChange: (sites: SiteInfo[]) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SiteInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Filters
  const [ownerFilter, setOwnerFilter] = useState('');
  const [minSizeMB, setMinSizeMB] = useState('');
  const [createdAfter, setCreatedAfter] = useState('');
  const [lastBefore, setLastBefore] = useState('');

  const selectedIds = new Set(selected.map((s) => s.id));

  async function doSearch() {
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const basic = await searchSites(query);
      setResults(basic);
      // Enrich owner/size in the background; keep basic results if it fails.
      const enriched = await enrichSites(basic);
      setResults(enriched);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }

  const minBytes = Number(minSizeMB) > 0 ? Number(minSizeMB) * 1048576 : 0;
  const createdAfterTs = createdAfter ? new Date(createdAfter).getTime() : null;
  const lastBeforeTs = lastBefore ? new Date(lastBefore).getTime() : null;

  const filtered = results.filter((s) => {
    if (ownerFilter && !(s.owner ?? '').toLowerCase().includes(ownerFilter.toLowerCase())) return false;
    if (minBytes > 0 && !(typeof s.sizeBytes === 'number' && s.sizeBytes >= minBytes)) return false;
    if (createdAfterTs !== null && !(s.createdDateTime && new Date(s.createdDateTime).getTime() >= createdAfterTs)) return false;
    if (lastBeforeTs !== null && !(s.lastActivity && new Date(s.lastActivity).getTime() <= lastBeforeTs)) return false;
    return true;
  });

  function toggle(site: SiteInfo) {
    if (selectedIds.has(site.id)) onChange(selected.filter((s) => s.id !== site.id));
    else onChange([...selected, site]);
  }

  function selectAllFiltered() {
    const map = new Map(selected.map((s) => [s.id, s]));
    for (const r of filtered) map.set(r.id, r);
    onChange([...map.values()]);
  }

  const hasFilters = !!(ownerFilter || minSizeMB || createdAfter || lastBefore);

  return (
    <div className="picker">
      <div className="picker__search">
        <input
          className="picker__input"
          placeholder="Search sites by name (e.g. HR, Finance) — or * for all"
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void doSearch();
          }}
        />
        <button className="btn btn--secondary btn--sm" onClick={() => void doSearch()} disabled={disabled || loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {error != null && <ErrorNotice error={error} />}

      {searched && !loading && results.length === 0 && !error && (
        <p className="small muted">No sites matched. Try a different keyword, or <code>*</code> to list all.</p>
      )}

      {results.length > 0 && (
        <>
          <div className="picker__filters">
            <input className="picker__filter" placeholder="Filter by owner" value={ownerFilter}
              disabled={disabled} onChange={(e) => setOwnerFilter(e.target.value)} />
            <input className="picker__filter" inputMode="numeric" placeholder="Min size (MB)" value={minSizeMB}
              disabled={disabled} onChange={(e) => setMinSizeMB(e.target.value.replace(/[^0-9]/g, ''))} />
            <label className="picker__datewrap">Created after
              <input className="picker__filter" type="date" value={createdAfter}
                disabled={disabled} onChange={(e) => setCreatedAfter(e.target.value)} />
            </label>
            <label className="picker__datewrap">Last activity before
              <input className="picker__filter" type="date" value={lastBefore}
                disabled={disabled} onChange={(e) => setLastBefore(e.target.value)} />
            </label>
          </div>

          <div className="picker__toolbar">
            <span className="small muted">
              {filtered.length} of {results.length} shown{hasFilters ? ' (filtered)' : ''}
            </span>
            <button className="btn btn--ghost btn--sm" onClick={selectAllFiltered} disabled={disabled || filtered.length === 0}>
              Select all {hasFilters ? 'filtered' : ''}
            </button>
          </div>

          <div className="picker__results">
            <table className="matrix picker__table">
              <thead>
                <tr>
                  <th style={{ width: 24 }}></th><th>Site</th><th>Owner</th><th>Size</th><th>Created</th><th>Last activity</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((site) => (
                  <tr key={site.id} className="picker__trow" onClick={() => toggle(site)}>
                    <td><input type="checkbox" checked={selectedIds.has(site.id)} disabled={disabled} readOnly /></td>
                    <td className="matrix__label">{site.displayName}</td>
                    <td className="matrix__detail">{site.owner ?? '—'}</td>
                    <td className="matrix__detail">{fmtSize(site.sizeBytes)}</td>
                    <td className="matrix__detail">{fmtDate(site.createdDateTime)}</td>
                    <td className="matrix__detail">{fmtDate(site.lastActivity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selected.length > 0 && (
        <div className="picker__selected">
          <span className="small muted">Selected ({selected.length}):</span>
          {selected.map((s) => (
            <button key={s.id} className="picker__chip" onClick={() => toggle(s)} disabled={disabled} title="Remove">
              {s.displayName} ✕
            </button>
          ))}
          <button className="btn btn--ghost btn--sm" onClick={() => onChange([])} disabled={disabled}>Clear</button>
        </div>
      )}
    </div>
  );
}
